import React from "react"
import { TableHeader, TableHeaderColumn, TableBody, TableRow, TableRowColumn, FontIcon } from "material-ui"
import { green500, grey500, red500 } from "material-ui/styles/colors"
import strings from "../../../localization/strings"
import { IWorkPoolEmployee } from "../../../models"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import ExternalLink from "../../common/externalLink/ExternalLink"
import Table from "../../common/Table"
import * as jobHelper from "../../../helpers/jobHelper"
import * as userHelper from "../../../helpers/userHelper"

interface IProps {
    tableName: string
    readonly: boolean
    singleSelectable?: boolean
    workPoolEmployees: IWorkPoolEmployee[]
    selectedCandidates?: IWorkPoolEmployee[]
    offset: number
    limit: number
    limitOptions: number[]
    total: number
    saveColumn?: boolean
    submittedByColumn?: boolean
    onPageClick(offset: number): void
    onLimitChange(limit: number): void
    shortListCandidate?(employeeId: string): void
    archiveCandidate?(employeeId: string): void
    handleSelectedCandidates?(workPoolEmployees: IWorkPoolEmployee[]): void
    assignEmployeeToShift: (employee: IWorkPoolEmployee) => void
}

const ShiftWorkPoolEmployeeTable: React.SFC<IProps> = (props: IProps) => {
    let notFoundString = ""
    if (props.tableName === "shortlist") notFoundString = strings.No_ShortList_Candidate_Found
    else if (props.tableName === "submitted") notFoundString = strings.No_Submitted_Candidate_Found
    else if (props.tableName === "requested") notFoundString = strings.No_Requested_Candidate_Found
    else if (props.tableName === "autoMatched") notFoundString = strings.No_Auto_Matched_Candidate_Found

    const missingBadges = (missingBadges) => {
        if (missingBadges && !missingBadges.length) {
            return (
                <div>
                    <FontIcon className="fa fa-check" style={{ fontSize: 18, color: green500 }} />
                    <span style={{ paddingLeft: 5, color: green500 }}>{strings.None}</span>
                </div>
            )
        } else if (missingBadges && missingBadges.length === 1) return missingBadges[0]
        else if (missingBadges && missingBadges.length > 1) return missingBadges.join(", ")
    }

    const specialties = (specialities) => {
        if (!specialities) {
            return null
        } else if (specialities && specialities.length === 1) return specialities[0]
        else if (specialities && specialities.length > 1) return specialities.join(", ")
    }

    const submittedByName = (submittedById, submittedByName) => {
        if (submittedById && !submittedByName) {
            return "Unknown"
        } else if (submittedById && submittedByName) {
            return submittedByName
        } else {
            return null
        }
    }

    return (
        <div style={{ paddingBottom: 15 }}>
            {props.workPoolEmployees.length === 0 ?
                <div>
                    <h2 style={{ textAlign: "center", marginTop: 25, fontSize: 15 }}>{notFoundString}</h2>
                </div> :
                <Table
                    readonly={props.readonly}
                    allowClickRow
                    items={props.workPoolEmployees}
                    handleSelect={props.handleSelectedCandidates}
                    total={props.total}
                    offset={props.offset}
                    limit={props.limit}
                    limitOptions={props.limitOptions}
                    onPageClick={props.onPageClick}
                    onLimitChange={props.onLimitChange}
                    singleSelectable={props.singleSelectable}
                >
                    <TableHeader>
                        <TableRow>
                            {props.tableName === "shortlist" &&
                                <TableHeaderColumn style={{ width: 25, paddingLeft: 5, paddingRight: 5 }} />
                            }
                            <TableHeaderColumn style={{ textAlign: "left" }}>{strings.Name}</TableHeaderColumn>
                            {props.submittedByColumn &&
                                <TableHeaderColumn style={{ textAlign: "left" }}>{strings.Submitted_By}</TableHeaderColumn>
                            }
                            <TableHeaderColumn style={{ textAlign: "left" }}>{strings.Missing_Badges}</TableHeaderColumn>
                            <TableHeaderColumn style={{ textAlign: "left" }}>{strings.Miles}</TableHeaderColumn>
                            {props.tableName === "shortlist" && [
                                <TableHeaderColumn key="email" style={{ textAlign: "left" }}>{strings.Email}</TableHeaderColumn>,
                                <TableHeaderColumn key="phone" style={{ textAlign: "left" }}>{strings.Cell_Phone}</TableHeaderColumn>
                            ]
                            }
                            {props.saveColumn &&
                                <TableHeaderColumn style={{ textAlign: "center" }}>{strings.Shortlist}</TableHeaderColumn>
                            }
                            {props.tableName === "requested" &&
                                <TableHeaderColumn style={{ textAlign: "center" }}>{strings.Decline}</TableHeaderColumn>
                            }
                            {props.tableName === "shortlist" &&
                                <TableHeaderColumn style={{ textAlign: "center" }}>{strings.Archive}</TableHeaderColumn>
                            }
                            <TableHeaderColumn style={{ textAlign: "center" }}>{strings.Specialties}</TableHeaderColumn>
                            <TableHeaderColumn style={{ textAlign: "center" }}>{strings.Occupations}</TableHeaderColumn>
                            <TableHeaderColumn style={{ textAlign: "center" }}>{strings.Assign_Employee_to_Shift}</TableHeaderColumn>
                        </TableRow>
                    </TableHeader>
                    <TableBody showRowHover={true} deselectOnClickaway={false}>
                        {props.workPoolEmployees.map((employee: IWorkPoolEmployee, key) =>
                            <TableRow key={key} selected={props.selectedCandidates && props.selectedCandidates.find(item => item.Id === employee.Id) ? true : false}>
                                {props.tableName === "shortlist" &&
                                    <TableRowColumn style={{ width: 25, paddingLeft: 5, paddingRight: 5, paddingTop: 4 }}>
                                        <ExternalLink
                                            url={"/user-details/" + employee.Id + "/" + userHelper.getValidSubPages()[0]}
                                        />
                                    </TableRowColumn>
                                }
                                <TableRowColumn title={employee.EmployeeName} style={{ textAlign: "left", cursor: "pointer" }}>{employee.EmployeeName}</TableRowColumn>
                                {props.submittedByColumn &&
                                    <TableRowColumn title={employee.SubmittedByName} style={{ textAlign: "left", cursor: "pointer" }}>{submittedByName(employee.SubmittedById, employee.SubmittedByName)}</TableRowColumn>
                                }
                                <TableRowColumn title={employee.MissingBadgeNames} style={{ textAlign: "left", cursor: "pointer" }}>{missingBadges(employee.MissingBadgeNames)}</TableRowColumn>
                                <TableRowColumn style={{ textAlign: "left", cursor: "pointer" }}>{jobHelper.getMilesFromClient(employee.MilesFromClient)}</TableRowColumn>
                                {props.tableName === "shortlist" && [
                                    <TableRowColumn key="email" title={employee.Email} style={{ textAlign: "left", cursor: "pointer" }}>{employee.Email}</TableRowColumn>,
                                    <TableRowColumn key="phone" title={employee.MobilePhoneNo} style={{ textAlign: "left", cursor: "pointer" }}>{employee.MobilePhoneNo}</TableRowColumn>
                                ]
                                }
                                {props.saveColumn &&
                                    <TableRowColumn title={strings.Save} style={{ textAlign: "center", cursor: "pointer" }}>
                                        <FontIcon
                                            className="fa fa-thumbs-up"
                                            style={{ fontSize: 20, color: grey500 }}
                                            onTouchTap={() => props.shortListCandidate(employee.Id)}
                                        />
                                    </TableRowColumn>
                                }
                                {props.tableName === "requested" &&
                                    <TableRowColumn style={{ textAlign: "center", cursor: "pointer" }}>
                                        {employee.Declined === false && <span style={{ paddingLeft: 5, color: green500 }}>{strings.Not_Declined}</span>}
                                        {employee.Declined && <span style={{ paddingLeft: 5, color: red500 }}>{strings.Declined}</span>}
                                    </TableRowColumn>
                                }
                                {props.tableName === "shortlist" &&
                                    <TableRowColumn title={strings.Archive} style={{ textAlign: "center", cursor: "pointer" }}>
                                        {employee.ArchivedByUserIds && !employee.ArchivedByUserIds.includes(AuthenticationStore.getLoggedInUser().Id) &&
                                            <FontIcon
                                                className="fa fa-thumbs-down"
                                                style={{ fontSize: 20, color: grey500 }}
                                                onTouchTap={() => props.archiveCandidate(employee.Id)}
                                            />
                                        }
                                    </TableRowColumn>
                                }
                                <TableRowColumn title={employee.Specialities} style={{ textAlign: "center" }}>{specialties(employee.Specialities)}</TableRowColumn>
                                <TableRowColumn title={employee.Occupation} style={{ textAlign: "center" }}>{employee.Occupation}</TableRowColumn>
                                <TableRowColumn title={strings.Assign_Candidate_to_Shift} style={{ textAlign: "center", cursor: "pointer" }}>
                                    <FontIcon
                                        className="fa fa-calendar-circle-user"
                                        style={{ fontSize: 20, color: grey500 }}
                                        onTouchTap={() => props.assignEmployeeToShift && props.assignEmployeeToShift(employee)}
                                    />
                                </TableRowColumn>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            }
        </div>
    )
}

export default ShiftWorkPoolEmployeeTable